import { Bot } from 'lucide-react';
import { create } from 'zustand';
import {
  checkCommandOptions,
  parseCommandArguments,
  resolveSubcommand,
  type CommandBranch,
  type CommandOption,
  type InteractionState,
  type ModalDefinition,
  type OptionValue,
  type SpaceCommand,
} from '@spawnwire/shared';
import type { MenuEntry } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';

/** Slash commands per space (fetched when someone types `/`) and the commands I ran that are still showing. */
interface CommandsState {
  bySpace: Record<string, SpaceCommand[]>;
  interactions: Record<string, InteractionState>;
  /** Forms bots opened for me, by the interaction that opened them, until I submit or close them. */
  forms: Record<string, OpenForm>;
}

export interface OpenForm {
  interactionId: string;
  channelId: string;
  botId: string;
  modal: ModalDefinition;
}

export const useCommands = create<CommandsState>(() => ({ bySpace: {}, interactions: {}, forms: {} }));

/**
 * The space's commands, once they are loaded. Components that build menus from `appMenuEntries` call this so they
 * draw again when the commands arrive (or a bot changes them).
 */
export function useSpaceCommands(spaceId: string | null | undefined): SpaceCommand[] | undefined {
  return useCommands((s) => (spaceId ? s.bySpace[spaceId] : undefined));
}

const loading = new Map<string, Promise<void>>();

export function loadCommands(spaceId: string, force = false): Promise<void> {
  if (!force && useCommands.getState().bySpace[spaceId]) return Promise.resolve();
  const running = loading.get(spaceId);
  if (running && !force) return running;
  const p = http
    .get<SpaceCommand[]>(`/spaces/${spaceId}/commands`)
    .then((list) => useCommands.setState((s) => ({ bySpace: { ...s.bySpace, [spaceId]: list } })))
    .catch(() => {})
    .finally(() => loading.delete(spaceId));
  loading.set(spaceId, p);
  return p;
}

/** A bot changed its commands: refetch where we already have them. */
export function commandsChanged(spaceId: string): void {
  if (useCommands.getState().bySpace[spaceId]) void loadCommands(spaceId, true);
}

/** How long to wait past a deadline before showing a failure ourselves, in case the server's update was lost. */
const GRACE_MS = 3000;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function watch(state: InteractionState): void {
  const old = timers.get(state.id);
  if (old) clearTimeout(old);
  timers.delete(state.id);
  if (state.status !== 'pending' && state.status !== 'deferred') return;
  const deadline = new Date(state.status === 'pending' ? state.respondBy : state.expiresAt).getTime() + GRACE_MS;
  timers.set(
    state.id,
    setTimeout(() => {
      timers.delete(state.id);
      const current = useCommands.getState().interactions[state.id];
      if (current && current.status === state.status) upsertInteraction({ ...current, status: 'failed', error: 'No answer came back from the bot.' });
    }, Math.max(0, deadline - Date.now())),
  );
}

/** How far each interaction has got. A fast bot can answer before the "pending" reply to running it arrives. */
const STAGE: Record<InteractionState['status'], number> = { pending: 0, deferred: 1, completed: 2, failed: 2 };
const reached = new Map<string, number>();

export function upsertInteraction(state: InteractionState): void {
  // Never step back: a late "pending" must not hide an answer (or bring back a dismissed notice).
  if (STAGE[state.status] < (reached.get(state.id) ?? -1)) return;
  reached.set(state.id, STAGE[state.status]);
  // A form the bot opened shows as a dialog; once it has been submitted (or taken back) the dialog goes.
  if (state.modal) {
    const opened = state.modal;
    useCommands.setState((s) => ({
      forms: { ...s.forms, [state.id]: { interactionId: state.id, channelId: state.channelId, botId: state.botId, modal: opened } },
    }));
  } else {
    closeForm(state.id);
  }
  // A visible reply arrives as a message, so a finished interaction without an ephemeral answer is done with.
  if (state.status === 'completed' && !state.ephemeral) {
    dismissInteraction(state.id);
    return;
  }
  useCommands.setState((s) => ({ interactions: { ...s.interactions, [state.id]: state } }));
  watch(state);
}

export function closeForm(interactionId: string): void {
  useCommands.setState((s) => {
    if (!s.forms[interactionId]) return {};
    const next = { ...s.forms };
    delete next[interactionId];
    return { forms: next };
  });
}

export function dismissInteraction(id: string): void {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  useCommands.setState((s) => {
    if (!s.interactions[id]) return {};
    const next = { ...s.interactions };
    delete next[id];
    return { interactions: next };
  });
}

/** `/name rest`: the command name typed so far, and whether its arguments have started. */
export function commandPrefix(text: string): { name: string; args: string | null } | null {
  const m = /^\/([a-z0-9_-]*)(?:\s([\s\S]*))?$/.exec(text);
  if (!m) return null;
  return { name: m[1]!, args: m[2] ?? (text.endsWith(' ') ? '' : null) };
}

/** The command a typed name means: the one picked from the list, or the only one with that name. */
export function pickCommand(commands: SpaceCommand[], name: string, pickedId: string | null): SpaceCommand | undefined {
  const same = commands.filter((c) => c.name === name);
  return same.find((c) => c.id === pickedId) ?? (same.length === 1 ? same[0] : undefined);
}

/**
 * Works out the subcommand and option values for a typed command; mentions must already be turned into ids.
 * `path` names the subcommand group and subcommand, when the command has them.
 */
export function commandValues(command: SpaceCommand, args: string): { values: Record<string, OptionValue>; path: string[] } | { error: string } {
  const step = resolveSubcommand(command, args);
  if (step.next) return { error: `Pick a subcommand: ${step.next.map((n) => n.name).join(', ')}.` };
  const parsed = parseCommandArguments({ options: step.options }, step.rest);
  if ('error' in parsed) return parsed;
  const checked = checkCommandOptions({ options: step.options }, parsed.values);
  return 'error' in checked ? checked : { values: checked.values, path: step.path };
}

/** Where the person is in typing a command: the subcommand path so far, what can come next, and the option being typed. */
export interface CommandStep {
  /** The subcommand group and subcommand typed so far. */
  path: string[];
  /** Subcommands or groups still to pick between, while the path is incomplete. */
  next: CommandBranch[] | null;
  /** The options to fill in at the end of the path. */
  options: CommandOption[];
  /** The option being typed. */
  current: CommandOption | null;
  /** While picking a subcommand: the partial name typed. */
  partial: string;
  /** What has been typed for the current option, and every value typed so far. */
  typed?: string;
  values?: Record<string, string>;
}

export function commandStep(command: SpaceCommand, args: string): CommandStep {
  const step = resolveSubcommand(command, args);
  if (step.next) {
    // Still choosing: the last word typed is a partial name.
    const partial = /(\S*)$/.exec(step.rest)?.[1] ?? '';
    return { path: step.path, next: step.next, options: [], current: null, partial };
  }
  const parsed = parseCommandArguments({ options: step.options }, step.rest);
  if ('error' in parsed) return { path: step.path, next: null, options: step.options, current: null, partial: '' };
  // While a value is being typed (no trailing space), it is still the current option.
  const filled = Object.keys(parsed.values);
  const typing = step.rest.length > 0 && !/\s$/.test(step.rest) ? filled.at(-1) : undefined;
  const current = step.options.find((o) => o.name === typing) ?? step.options.find((o) => parsed.values[o.name] === undefined) ?? null;
  return { path: step.path, next: null, options: step.options, current, partial: '', typed: current ? parsed.values[current.name] : undefined, values: parsed.values };
}

/** The option the person is about to type: the first one without a value yet. */
export function nextOption(command: SpaceCommand, args: string): CommandOption | null {
  return commandStep(command, args).current;
}

/** What a bot suggests for the option being typed. Empty if it is slow or offline. */
export async function requestSuggestions(channelId: string, command: SpaceCommand, path: string[], option: string, value: string, others: Record<string, string>): Promise<{ name: string; value: string | number }[]> {
  try {
    const res = await http.post<{ choices: { name: string; value: string | number }[] }>(`/channels/${channelId}/autocomplete`, { commandId: command.id, subcommand: path, option, value: value.slice(0, 100), options: others });
    return res.choices;
  } catch {
    return [];
  }
}

/**
 * Menu entries for the user and message commands bots offer here ("Apps"), for a person's or a message's menu.
 * Running one sends the bot who or what it was picked from; its answer shows like any command's.
 */
export function appMenuEntries(spaceId: string | null, kind: 'user' | 'message', targetId: string, channelId?: string): MenuEntry[] {
  if (!spaceId) return [];
  const here = channelId ?? /^\/app\/[^/]+\/([^/?#]+)/.exec(location.pathname)?.[1];
  const channel = here ? useStore.getState().channels[here] : undefined;
  if (!here || !channel || !['text', 'announcement', 'thread'].includes(channel.type)) return [];
  const commands = (useCommands.getState().bySpace[spaceId] ?? []).filter((c) => c.type === kind);
  return commands.map((c, i) => ({
    label: `${c.name} · ${c.bot.displayName}`,
    icon: Bot,
    separatorBefore: i === 0,
    onSelect: () => {
      http
        .post<InteractionState>(`/channels/${here}/interactions`, { commandId: c.id, targetId })
        .then(upsertInteraction)
        .catch((err) => ui.toast(errorMessage(err), 'error'));
    },
  }));
}
